/**
 * ExpenseFormDialog - Premium expense form
 *
 * Condensed, focused form with smart defaults and excellent UX
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useLedgerToast } from '@/hooks/useLedgerToast';
import {
  CreateAllocation,
  CreateExpense,
  ReadExpense,
  useExpenseCreateMutation,
  useExpenseReplaceAllocationsMutation,
  useExpenseUpdateMutation,
  useLienListQuery,
} from '@/services/api/ledgerService';
import { useGetMedicalProvidersQuery } from '@/services/api/treatmentService';

import { AllocationEditor } from './AllocationEditor';

interface ExpenseFormDialogProps {
  matterId: string;
  expense?: ReadExpense;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Form validation schema
const expenseFormSchema = z.object({
  provider_id: z.string().min(1, 'Provider is required'),
  service_date: z
    .string()
    .min(1, 'Service date is required')
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format')
    .refine((date) => !Number.isNaN(Date.parse(date)), 'Invalid date'),
  billed_amount: z
    .string()
    .min(1, 'Billed amount is required')
    .refine((val) => {
      const cleaned = val.replace(/[^0-9.]/g, '');
      const num = parseFloat(cleaned);
      return !Number.isNaN(num) && num >= 0;
    }, 'Must be a valid amount'),
  related_to_incident: z.boolean(),
});

type ExpenseFormValues = z.infer<typeof expenseFormSchema>;

export const ExpenseFormDialog = ({
  matterId,
  expense,
  open,
  onOpenChange,
}: ExpenseFormDialogProps) => {
  const { showSuccess, showError } = useLedgerToast();
  const isEditMode = !!expense;

  // Allocation state - default to single UNALLOCATED allocation
  const [allocations, setAllocations] = useState<CreateAllocation[]>(() => [
    {
      allocation_bucket: 'UNALLOCATED',
      payer_type: undefined,
      payer_id: undefined,
      amount: '0.00',
    },
  ]);

  // API hooks
  const { data: providers = [] } = useGetMedicalProvidersQuery(matterId, {
    skip: !matterId,
  });
  const { data: liens = [] } = useLienListQuery({ matterId });
  const [createExpense, { isLoading: isCreating }] = useExpenseCreateMutation();
  const [updateExpense, { isLoading: isUpdating }] = useExpenseUpdateMutation();
  const [replaceAllocations, { isLoading: isReplacingAllocations }] =
    useExpenseReplaceAllocationsMutation();

  const isSubmitting = isCreating || isUpdating || isReplacingAllocations;

  // Form setup
  const form = useForm<ExpenseFormValues>({
    resolver: zodResolver(expenseFormSchema),
    defaultValues: {
      provider_id: '',
      service_date: format(new Date(), 'yyyy-MM-dd'),
      billed_amount: '',
      related_to_incident: true,
    },
  });

  // Check if selected provider has an active lien
  const selectedProviderId = form.watch('provider_id');
  const providerHasLien = useMemo(() => {
    if (!selectedProviderId) return false;
    return liens.some(
      (lien) =>
        lien.lienholder_type === 'PROVIDER' &&
        lien.lienholder_provider?.id === selectedProviderId &&
        lien.status === 'ACTIVE',
    );
  }, [selectedProviderId, liens]);

  // Initialize form when expense changes (edit mode)
  useEffect(() => {
    if (expense) {
      form.reset({
        provider_id: expense.provider_id,
        service_date: expense.service_date,
        billed_amount: expense.billed_amount,
        related_to_incident: expense.related_to_incident,
      });

      // Initialize allocations from expense
      const existingAllocations: CreateAllocation[] = expense.allocations.map(
        (alloc) => ({
          allocation_bucket: alloc.allocation_bucket,
          payer_type: alloc.payer_type,
          payer_id:
            alloc.payer_provider?.id ?? alloc.payer_coverage?.id ?? undefined,
          amount: alloc.amount,
        }),
      );
      setAllocations(
        existingAllocations.length > 0
          ? existingAllocations
          : [
              {
                allocation_bucket: 'UNALLOCATED',
                payer_type: undefined,
                payer_id: undefined,
                amount: expense.billed_amount,
              },
            ],
      );
    } else {
      form.reset({
        provider_id: '',
        service_date: format(new Date(), 'yyyy-MM-dd'),
        billed_amount: '',
        related_to_incident: true,
      });
      setAllocations([
        {
          allocation_bucket: 'UNALLOCATED',
          payer_type: undefined,
          payer_id: undefined,
          amount: '0.00',
        },
      ]);
    }
  }, [expense, form]);

  // Auto-update allocations based on provider lien status and amount
  const billedAmount = form.watch('billed_amount');
  useEffect(() => {
    if (!isEditMode && allocations.length === 1) {
      const amount = billedAmount || '0';

      // If provider has lien, auto-set to PROVIDER_DEFERRED_PAYMENT
      if (providerHasLien && selectedProviderId) {
        setAllocations([
          {
            allocation_bucket: 'PROVIDER_DEFERRED_PAYMENT',
            payer_type: 'PROVIDER',
            payer_id: selectedProviderId,
            amount,
          },
        ]);
      } else if (allocations[0].allocation_bucket === 'UNALLOCATED') {
        // Otherwise keep as UNALLOCATED with updated amount
        setAllocations([
          {
            allocation_bucket: 'UNALLOCATED',
            payer_type: undefined,
            payer_id: undefined,
            amount,
          },
        ]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billedAmount, isEditMode, providerHasLien, selectedProviderId]);

  // Validate allocations sum and payers
  const allocationsValid = useMemo(() => {
    const billedNum = parseFloat(billedAmount) || 0;
    const allocatedSum = allocations.reduce(
      (sum, alloc) => sum + (parseFloat(alloc.amount) || 0),
      0,
    );
    const sumsMatch = Math.abs(billedNum - allocatedSum) < 0.01;

    // Check all allocations have payers when needed
    const allHavePayers = allocations.every((alloc) => {
      // UNALLOCATED and CLIENT don't need a payer
      if (
        alloc.allocation_bucket === 'UNALLOCATED' ||
        alloc.allocation_bucket === 'CLIENT_SELF_PAYMENT'
      ) {
        return true;
      }
      // All others need a payer_id
      return !!alloc.payer_id;
    });

    return sumsMatch && allHavePayers;
  }, [billedAmount, allocations]);

  // Submit handler
  const onSubmit = useCallback(
    async (values: ExpenseFormValues) => {
      if (allocations.length > 0 && !allocationsValid) {
        form.setError('billed_amount', {
          message: 'Allocations must equal the billed amount',
        });
        return;
      }

      try {
        if (isEditMode && expense) {
          // Update expense details
          await updateExpense({
            matterId,
            id: expense.id,
            updateExpense: {
              service_date: values.service_date,
              billed_amount: values.billed_amount,
              related_to_incident: values.related_to_incident,
            },
          }).unwrap();

          // Update allocations
          await replaceAllocations({
            matterId,
            id: expense.id,
            createAllocationList: allocations,
          }).unwrap();

          showSuccess('update', 'expense');
        } else {
          const createData: CreateExpense = {
            provider_id: values.provider_id,
            service_date: values.service_date,
            billed_amount: values.billed_amount,
            description: 'Medical expense',
            related_to_incident: values.related_to_incident,
            allocations: allocations.length > 0 ? allocations : undefined,
          };

          await createExpense({
            matterId,
            createExpense: createData,
          }).unwrap();

          showSuccess('create', 'expense');
        }

        onOpenChange(false);
      } catch {
        showError();
      }
    },
    [
      isEditMode,
      expense,
      matterId,
      allocations,
      allocationsValid,
      createExpense,
      updateExpense,
      replaceAllocations,
      showSuccess,
      showError,
      onOpenChange,
      form,
    ],
  );

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isSubmitting) {
        onOpenChange(isOpen);
      }
    },
    [isSubmitting, onOpenChange],
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl p-0">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            {/* Compact Header */}
            <div className="px-6 py-4 border-b border-slate-100">
              <DialogTitle className="text-lg font-semibold text-slate-900">
                {isEditMode ? 'Edit Expense' : 'New Expense'}
              </DialogTitle>
            </div>

            {/* Form Fields - Clean and Simple */}
            <div className="p-6 space-y-4">
              {/* Provider - Full Width, Clear */}
              <FormField
                control={form.control}
                name="provider_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">
                      Provider
                      {providerHasLien && (
                        <span className="ml-2 text-xs text-red-600 font-semibold">
                          (On Lien)
                        </span>
                      )}
                    </FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                      disabled={isEditMode}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select provider..." />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {providers.map((provider) => {
                          const hasLien = liens.some(
                            (lien) =>
                              lien.lienholder_type === 'PROVIDER' &&
                              lien.lienholder_provider?.id === provider.id &&
                              lien.status === 'ACTIVE',
                          );
                          return (
                            <SelectItem key={provider.id} value={provider.id}>
                              {provider.name ?? 'Unnamed Provider'}
                              {hasLien && (
                                <span className="ml-2 text-red-600 text-xs">
                                  (Lien)
                                </span>
                              )}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Date and Amount - Side by Side */}
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="service_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium">
                        Service Date
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="date"
                          value={field.value}
                          onChange={(e) => field.onChange(e.target.value)}
                          max={format(new Date(), 'yyyy-MM-dd')}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="billed_amount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium">
                        Billed Amount
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none">
                            $
                          </span>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="0.00"
                            className="pl-7 tabular-nums"
                            value={field.value}
                            onChange={(e) => field.onChange(e.target.value)}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Allocations */}
              <div className="pt-2">
                <AllocationEditor
                  allocations={allocations}
                  billedAmount={form.watch('billed_amount') || '0'}
                  onChange={setAllocations}
                  matterId={matterId}
                  expenseProviderId={form.watch('provider_id')}
                  expenseProviderName={
                    providers.find((p) => p.id === form.watch('provider_id'))
                      ?.name ?? ''
                  }
                  providerHasLien={providerHasLien}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100">
              <Button
                type="button"
                variant="ghost"
                onClick={() => handleOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || !allocationsValid}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {(() => {
                  if (isSubmitting) {
                    return (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    );
                  }
                  return isEditMode ? 'Save' : 'Add Expense';
                })()}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
